import {Box, SxProps, Theme} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {LocationOnOutlined} from '@mui/icons-material';
import {ChangeEvent, FC, useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {locationsApi} from 'src/api/locations';
import {useBusinessId, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {LocationCreateFormWrapper} from 'src/components/locations/LocationCreateFormWrapper';
import {LocationStatistic} from 'src/components/locations/LocationStatistic';
import {LocationTable} from 'src/components/locations/LocationTable';
import {LocationStatsStrip} from 'src/components/locations/LocationStatsStrip';
import {LocationGap} from 'src/components/locations/LocationStatsStrip/LocationStatsStrip.types';
import {UserTypeEnum} from 'src/enums';
import {LocationSortFieldEnum, LocationType} from 'src/enums/locations';
import {DataType, ListParamsType} from 'src/types';
import {LocationSearchRequestData} from 'src/types/locations';
import {useListQueryState} from 'src/hooks/useListQueryState';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening one location and coming back restores the view instead of resetting it.
 *
 * `gap` is one name where the request carries two flags - `withoutChargers` and `withoutPrice` -
 * because the two are alternatives and only one can be chosen.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    country: '',
    city: '',
    locationType: '',
    gap: '',
    sortField: LocationSortFieldEnum.ID as string,
    sortAsc: true,
};

/**
 * The map, the block and the list are three cards on one page, spaced like every other stack.
 *
 * The clearance under the app bar sits on the map card, which is what comes first here.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};

/**
 * The table carries its own top margin on top of the stack's gap, so without this the block sat
 * closer to the map than the list sits to the block. The same margin here makes the two gaps equal.
 */
const stripSpacingSx: SxProps<Theme> = {
    mt: 2.5,
};

type LocationMutationTrigger = ReturnType<typeof locationsApi.useLazyGetLocationsQuery>[0];

/**
 * The body both the list and the block above it are asked for.
 * <p>
 * Shared rather than written twice: the block counts the set the list shows, and two copies of
 * "which filters go in" is exactly how the two come to disagree.
 */
export const buildLocationsSearchData = (
    tableData: LocationSearchRequestData,
    businessId?: number,
    companyId?: number
): LocationSearchRequestData => {
    const newData: DataType = {
        ...tableData,
        businessId: tableData.businessId ? tableData.businessId : businessId,
        companyId: tableData.companyId ? tableData.companyId : companyId,
    };

    Object.keys(tableData).map((item: string) => {
        !newData[item] && delete newData[item];
    });

    return {...newData};
};

export const handleGetLocations =
    (trigger: LocationMutationTrigger, businessId?: number, companyId?: number) =>
        async (tableData: LocationSearchRequestData, params: ListParamsType) => {
            // No page-wide preloader. This is one list on a page that has other things worth reading -
            // the counts above it arrive from their own request - and a full-screen spinner made all
            // of them wait for the slowest. The table says it is loading, in the space it already
            // occupies.

            const data: LocationSearchRequestData = buildLocationsSearchData(
                tableData, businessId, companyId);

            try {
                return await trigger({data, params}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            }
            return undefined;
        };

export const Locations: FC = () => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const persona = usePersona();
    const [open, setOpen] = useState<boolean>(false);
    const dialogTitle = intl.formatMessage({id: 'createNewLocation'});
    const {authInfo} = useAppSelector((state) => state.common);
    const [trigger, response] = locationsApi.useLazyGetLocationsQuery();

    const isRealEstate = useDefineIsRealEstateRole();
    const isCompanyUser = persona.isCompany;
    const isDriver = persona.isDriver || authInfo.userType === UserTypeEnum.PRIVATE_DRIVER;

    // Hides the page's add button, so it asks whether they may add one.
    // Three personas were named here and a fourth was not; locations.create excludes all of them
    // and keeps excluding whatever is added next.
    const canCreateLocation = useCan('locations.create');

    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);

    // A boolean rather than `authInfo` itself: the effect below waits for the session to arrive, and
    // depending on the object would re-ask the server every time anything in it was replaced.
    const isSignedIn = Boolean(Object.keys(authInfo).length);

    // Both derived from the address bar rather than kept beside it.
    const filterParams: LocationSearchRequestData = useMemo(() => ({
        businessId: 0,
        city: query.city,
        country: query.country,
        keyword: query.keyword,
        locationType: query.locationType || undefined,
        withoutChargers: query.gap === 'withoutChargers' ? true : undefined,
        withoutPrice: query.gap === 'withoutPrice' ? true : undefined,
        sortAsc: query.sortAsc,
        sortField: query.sortField,
    }), [query]);

    const tableParams: ListParamsType = useMemo(
        () => ({page: query.page, size: query.size}),
        [query.page, query.size]
    );

    const handleOpenModal = () => {
        setOpen(true);
    };
    const handleCloseModal = () => {
        setOpen(false);
    };

    // The tiles' own filters are left out of the key: the server ignores them anyway, and keeping
    // them out means clicking a tile does not refetch numbers that cannot have changed.
    const companyId = authInfo?.company?.id || authInfo?.employee?.company?.id;
    const statsRequest = useMemo(
        () => ({
            data: {
                ...buildLocationsSearchData(filterParams, businessId, companyId),
                locationType: undefined,
                withoutChargers: undefined,
                withoutPrice: undefined,
            },
        }),
        [filterParams, businessId, companyId]
    );
    const {data: locationStats} = locationsApi.useGetLocationStatsQuery(statsRequest, {
        skip: !Object.keys(authInfo).length,
    });

    /**
     * Whatever the list is now asking for, as a whole request body from the table below.
     *
     * Back to the first page, because the row that was on page four of the old filter is not on page
     * four of the new one. The two gap flags come back as the one name they stand for.
     */
    const handleChange = (value: LocationSearchRequestData) => {
        setQuery({
            ...query,
            page: 0,
            keyword: value.keyword ?? '',
            country: value.country ?? '',
            city: value.city ?? '',
            locationType: String(value.locationType ?? ''),
            gap: value.withoutChargers
                ? 'withoutChargers'
                : value.withoutPrice
                    ? 'withoutPrice'
                    : '',
            sortField: value.sortField ?? LocationSortFieldEnum.ID,
            sortAsc: value.sortAsc ?? true,
        });
    };

    // Type and gap are alternatives, not filters to combine: each selection clears the other, and
    // clicking the active one clears it.
    const handleSelectType = (type: LocationType) => {
        setQuery({
            ...query,
            page: 0,
            gap: '',
            locationType: query.locationType === type ? '' : type,
        });
    };

    const handleSelectGap = (gap: LocationGap) => {
        setQuery({
            ...query,
            page: 0,
            locationType: '',
            gap: query.gap === gap ? '' : gap,
        });
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => setQuery({
        ...query,
        page: 0,
        size: +e.target.value || DEFAULT_PAGE_SIZE,
    });

    // Memoised so the effect below can depend on it: `trigger` is stable, and the business or the
    // company only changes when the session does, which has to re-ask anyway.
    const callHandler = useMemo(
        () => handleGetLocations(
            trigger,
            businessId,
            authInfo?.company?.id || authInfo?.employee?.company?.id
        ),
        [trigger, businessId, authInfo?.company?.id, authInfo?.employee?.company?.id]
    );

    /**
     * One place asks the server, whenever the address bar says something else.
     *
     * Every handler above only writes the URL. Before this each of them called the server itself and
     * a mount effect had to put the state back together from `location.state` and a regex over
     * `location.search` - so arriving with a filter already in the URL, which is what Back and a
     * reload both are, fetched the unfiltered list.
     *
     * No driver branch. It used to filter by authInfo.location and, when the account had none, skip
     * the request entirely and render an empty table - which is what a driver saw, because
     * user_account.location_id is only set when the parking address they typed matched an existing
     * location exactly. The server decides the slice now: it narrows locations to the ones this
     * driver is assigned to or has a charger on, so asking is both safe and the only way to see the
     * second kind at all.
     */
    useEffect(() => {
        if (!isSignedIn) {
            return;
        }
        void callHandler(filterParams, tableParams);
    }, [isSignedIn, callHandler, filterParams, tableParams]);

    return (
        <Box sx={pageSx}>
            {response.data && (
                <>
                    <LocationStatistic
                        filterParams={filterParams}
                    />
                    <Box sx={stripSpacingSx}>
                        <LocationStatsStrip
                            data={locationStats}
                            activeType={filterParams.locationType}
                            onSelectType={handleSelectType}
                            activeGap={
                                filterParams.withoutChargers
                                    ? 'withoutChargers'
                                    : filterParams.withoutPrice
                                        ? 'withoutPrice'
                                        : undefined
                            }
                            onSelectGap={handleSelectGap}
                        />
                    </Box>
                    <LocationTable
                        filters={filterParams}
                        loading={response.isFetching}
                        data={response.data || {
                            content: [],
                            page: {
                                totalPages: 0,
                                totalElements: 0,
                                size: 0,
                                number: 0
                            }
                        }}
                        size={tableParams.size}
                        pageNumber={tableParams.page}
                        handleUpdateTableData={handleChange}
                        handleChangePage={handleChangePage}
                        handleChangeRowsNumber={handleChangeRowsNumber}
                        isCompanyUser={isCompanyUser}
                        isDriverUser={isDriver}
                        title={intl.formatMessage({id: 'locations'})}
                        onAddClick={handleOpenModal}
                        buttonText="addLocation"
                        onlyTitle={!canCreateLocation}
                        icon={<LocationOnOutlined/>}
                    />
                </>
            )}
            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <LocationCreateFormWrapper
                    handleClose={handleCloseModal}
                    authInfo={authInfo}
                />
            </AMPModal>
        </Box>
    );
};
